import {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {
  type RunSelectorGroupsLeanQuery,
  type RunSelectorRunsQuery,
} from '../../../generated/graphql';
import {useProjectParams} from '../../../pages/ProjectPage/useProjectParams';
import {trackCrossProjectPinnedRuns} from '../../../services/analytics/crossProjectRunsEvents';
import {useSelector} from '../../../state/hooks';
import {useSyncedPollingSubscriber} from '../../../state/polling/hooks';
import * as RunSelectorRunActions from '../../../state/runSelectorRuns/actions';
import {
  EMPTY_DATA,
  type LeanRunSelectorGroupRunMetadata,
} from '../../../state/runSelectorRuns/reducer';
import {useCachedDataWithOrigin} from '../../../util/cache';
import {useFirstRumTiming} from '../../../util/datadog/datadogRumTimings';
import {extractRunFilterErrorMessage} from '../../../util/errors';
import type * as RunTypes from '../../../util/runTypes';
import {type RunSelectorRunsPartialVars} from '../types';
import {isCrossProjectRun} from '../util';
import {
  useRunSelectorRunsDataQuery,
  useVariables,
} from './useRunSelectorRunsDataQuery';
import {
  isOtherPageLoaded,
  type RunsPagingMode,
  useRunsPaging,
} from './useRunsPaging';

export type UseRunSelectorRunsArgs = {
  /** Indicates whether this is a root query for an entire run set, or a sub-query for an expanded group */
  isRootQuery: boolean;
  /** Unique ID for the runs data managed by this instance of useRunSelectorRuns */
  queryId: string;
  /** Server ID of the view, used to correlate workspace-load analytics */
  viewId?: string;
  /** Runs per page; with `offset` paging each request fetches one page of this size */
  pageSize: number;
  /** The page shown, when the caller tracks it (expanded groups); the root query takes its page through onPageChange instead */
  page?: number;
  /** Whether each request fetches only the page shown or every run through it; see RunsPagingMode */
  pagingMode: RunsPagingMode;
  /** Variables that will be used to compute the full set of query variables */
  partialVariables: RunSelectorRunsPartialVars;
  /** If true, filters will be stricter so only selected (visualized) runs are returned */
  shouldOnlyShowSelected?: boolean;
  /** If true, will not query for runs */
  skip?: boolean;
  /**
   * If true (and there is an active grouping), grouped rows are fetched via
   * the lean runSelectorGroups query instead of the full runs query. See
   * useLeanGroupedRunsQuery in useLeanGroupedRunsQuery.ts.
   */
  useLeanGroupedRunsQuery?: boolean;
};

type RunSelectorRunsResult = {
  /**
   * Indicates whether we're loading runs. Could be from explicit user
   * interactions (e.g. searching, paging, ...) or automatic polling.
   */
  isLoadingRuns: boolean;
  /**
   * Indicates whether we're loading for a polled request.
   * Allows more granular control for loading UI.
   * @todo update type to be required once off of runs-low
   */
  isLoadingRunsPolled?: boolean;
  /**
   * Function for paging. When navigating between pages,
   * call this with the new page number to ensure runs for
   * that page will be loaded (as necessary; may be cached)
   * @todo update type to be required once off of runs-low
   */
  loadMoreRuns?: (page: number) => void;
  /**
   * Call with every page the table shows so only that page's runs are fetched.
   * Set only for `offset` paging, and unused when the caller passes `page`.
   */
  onPageChange?: (page: number) => void;
  /**
   * Detailed runs data: the current page with `offset` paging, every run through it otherwise. Rows synthesized from the lean
   * grouped query additionally carry their group values in
   * `__runSelectorGroupValues`.
   */
  runs: Array<RunTypes.Run & LeanRunSelectorGroupRunMetadata>;
  /**
   * Pinned and baseline runs
   */
  pinnedRuns: RunTypes.Run[];
  /**
   * Total number of runs that would match the query,
   * regardless of the paging limit
   */
  totalRuns: number;
  /**
   * User-facing message when the runs query was rejected for an invalid run
   * filter (e.g. a non-RE2 regex in run search). Undefined otherwise.
   */
  runFilterErrorMessage?: string;
};

/**
 * Data flow in this hook:
 * 1. When a root query mounts, check the cache for previously fetched data
 * 2. If we don't have query data but we have cache data we can put that into redux
 * 3. When the query returns, write the new results into redux and
 * 4. update the root query's cache any time a new query response is received
 */
export const useRunSelectorRuns = ({
  isRootQuery,
  queryId,
  viewId,
  pageSize,
  page,
  pagingMode,
  partialVariables,
  shouldOnlyShowSelected,
  skip = false,
  useLeanGroupedRunsQuery = false,
}: UseRunSelectorRunsArgs): RunSelectorRunsResult => {
  const {after, limit, onPageChange} = useRunsPaging(
    pageSize,
    pagingMode,
    page
  );
  const isOffsetPaging = pagingMode === 'offset';
  // small normalizer to ensure the page is recorded in the correct format
  const recordedPage = (
    vars?: RunSelectorRunActions.RunSelectorRunsPage
  ): RunSelectorRunActions.RunSelectorRunsPage | undefined =>
    isOffsetPaging
      ? {after: vars?.after ?? null, limit: vars?.limit}
      : undefined;

  // Child queries for expanded groups inherit the flag from the top-level
  // query but fall back to the full runs query once all grouping keys have
  // been converted to filters (i.e. when fetching individual runs).
  const isLeanGroupedRunsQueryActive =
    useLeanGroupedRunsQuery && partialVariables.grouping.length > 0;

  const dispatch = useDispatch();

  // Fire a one-time analytics event when a workspace finishes loading with
  // pinned cross-project runs. Guarded with a ref to avoid re-firing on polls.
  const {entityName: urlEntityName, projectName: urlProjectName} =
    useProjectParams();
  const hasTrackedWorkspaceLoadRef = useRef(false);

  const onQueryCompleted = (
    data: RunSelectorRunsQuery,
    vars?: RunSelectorRunActions.RunSelectorRunsPage & {
      pinnedRunIds?: string[];
    }
  ) => {
    if (!data?.project && !data?.pinnedRuns) {
      return;
    }

    const requestedPinnedRunIds = vars?.pinnedRunIds ?? [];
    const pinnedRuns = data?.pinnedRuns?.runs ?? [];
    const notPinnedRuns = data?.project?.runs ?? {
      delta: [],
      totalCount: 0,
      deltaOrder: [],
    };

    const workspaceProject = {
      entityName: urlEntityName,
      name: urlProjectName,
    };
    const crossProjectPinnedRuns = pinnedRuns.filter(run =>
      isCrossProjectRun(run.project ?? undefined, workspaceProject)
    );

    dispatch(
      RunSelectorRunActions.processQueryResults(
        queryId,
        requestedPinnedRunIds,
        {pinnedRuns, runs: notPinnedRuns},
        recordedPage(vars)
      )
    );

    // Analytics
    if (
      isRootQuery &&
      !hasTrackedWorkspaceLoadRef.current &&
      crossProjectPinnedRuns.length > 0
    ) {
      hasTrackedWorkspaceLoadRef.current = true;
      const distinctProjectKeys = new Set(
        crossProjectPinnedRuns.map(
          run => `${run.project!.entityName}/${run.project!.name}`
        )
      );
      trackCrossProjectPinnedRuns({
        action: 'Workspace with cross-project runs loaded',
        viewId,
        runSetId: queryId,
        numForeignRuns: crossProjectPinnedRuns.length,
        numDistinctProjects: distinctProjectKeys.size,
      });
    }
  };

  const onLeanGroupQueryCompleted = (
    data: RunSelectorGroupsLeanQuery | undefined,
    vars?: RunSelectorRunActions.RunSelectorRunsPage & {groupKeys?: string[]}
  ) => {
    const runSelectorGroups = data?.project?.runSelectorGroups;
    dispatch(
      RunSelectorRunActions.processLeanGroupResults(
        queryId,
        vars?.groupKeys ?? [],
        {
          groups: runSelectorGroups?.edges.map(edge => edge.node) ?? [],
          totalCount: runSelectorGroups?.totalCount ?? 0,
        },
        recordedPage(vars)
      )
    );
  };

  const {variables, deltaVariablesRef} = useVariables({
    after,
    limit,
    partialVariables,
    shouldOnlyShowSelected,
    useLeanGroupedRunsQuery: isLeanGroupedRunsQueryActive,
  });
  const {isLoadingRuns, refetch, error} = useRunSelectorRunsDataQuery({
    variables,
    pagingMode,
    useLeanGroupedRunsQuery: isLeanGroupedRunsQueryActive,
    skip,
    deltaVariablesRef,
    onQueryCompleted,
    onLeanGroupQueryCompleted,
  });
  const runFilterErrorMessage = extractRunFilterErrorMessage(error);

  // Note: Redux is the source of truth for runs data
  // make sure that only redux data is returned from this hook
  // returning data from the query OR the cache opens up race condition bugs
  const dataFromRedux = useSelector(state => state.runSelectorRuns[queryId]);
  const {
    runs,
    page: loadedPage,
    pinnedRuns,
    totalRuns,
    lastUpdatedAt,
  } = dataFromRedux ?? EMPTY_DATA;

  // Only root snapshots can seed Redux; child snapshots have no hydration path.
  const {data: dataFromCache, origin} = useCachedDataWithOrigin<
    typeof dataFromRedux
  >(
    {
      namespace: 'RunsSelectorRunsData',
      key: variables,
      dataFromServer: dataFromRedux,
      waitingForDataFromServer: isLoadingRuns && isRootQuery,
    },
    !isRootQuery
  );

  useEffect(() => {
    /**
     * Since Redux is the source of truth, we only want to load data into Redux from the cache a single time when:
     * 1. data is coming back from the cache, and
     * 2. we don't already have data loaded into redux from a query
     *
     * If the cache can get looped into overwriting Redux when its data conflicts with redux, then the possibility for stale cache writes to UI state is created. If the cache can ONLY seed empty redux data, then stale cache writes will always be soon overwritten by query results being dispatched to Redux (and ultimately syndicated down into the cache)
     */
    if (dataFromCache != null && dataFromRedux == null && isRootQuery) {
      dispatch(
        RunSelectorRunActions.loadRunSelectorRunsFromCache(
          queryId,
          dataFromCache
        )
      );
    }
  }, [dispatch, queryId, dataFromCache, origin, dataFromRedux, isRootQuery]);

  // Clear redux state on unmount/group change
  useEffect(() => {
    return () => {
      if (!skip) {
        dispatch(RunSelectorRunActions.removeQuery(queryId));
      }
    };
  }, [dispatch, queryId, skip]);

  // Perf logging
  const isInitialLoadedRef = useRef(false);
  useFirstRumTiming('run_selector_initial_loaded', {
    when: () => {
      if (isRootQuery && dataFromRedux != null && !isInitialLoadedRef.current) {
        isInitialLoadedRef.current = true;
        return true;
      }
      return false;
    },
  });

  // Keep delta vars ref up to date
  useEffect(() => {
    if (isLeanGroupedRunsQueryActive) {
      // Lean group rows are synthetic, so there is no run delta to compute.
      deltaVariablesRef.current = {
        currentRuns: [],
        lastUpdated: new Date(0).toISOString(),
      };
      return;
    }

    deltaVariablesRef.current = {
      currentRuns: runs.map(run => run.id),
      lastUpdated: new Date(lastUpdatedAt).toISOString(),
    };
  }, [runs, lastUpdatedAt, deltaVariablesRef, isLeanGroupedRunsQueryActive]);

  // Subscribe to synced polling so RunSelector updates in sync with Charts
  const {lastTickedAt, stopTaskTimer} =
    useSyncedPollingSubscriber('RunSelector');
  const [isPollTaskActive, setIsPollTaskActive] = useState(false);

  // Refetch on poll tick
  useEffect(() => {
    if (!skip && lastTickedAt != null && !isLoadingRuns) {
      setIsPollTaskActive(true);
      refetch();
    }
  }, [lastTickedAt]); // oxlint-disable-line react-hooks/exhaustive-deps -- should ONLY trigger on poll tick!

  // Record when polled requests are loaded
  useEffect(() => {
    if (!isLoadingRuns && isPollTaskActive) {
      setIsPollTaskActive(false);
      stopTaskTimer();
    }
  }, [isPollTaskActive, isLoadingRuns, stopTaskTimer]);

  // a growing limit keeps every earlier run, so a larger limit still holds the page shown
  const isPageMismatched =
    isOffsetPaging &&
    isOtherPageLoaded({
      hasRequestFailed: error != null,
      isRootQuery,
      loadedPage,
      requestedPage: variables,
    });
  // use mismatch as signal to return empty runs as we are awaiting the new page
  const pageRuns = isPageMismatched ? EMPTY_DATA.runs : runs;
  // a failed or empty response never records the new page, so the mismatch only counts as loading while a request is in flight
  const isLoadingPage =
    isLoadingRuns && (origin === 'server' || isPageMismatched);
  const loadMoreRuns = isOffsetPaging ? undefined : onPageChange;
  const onOffsetPageChange = isOffsetPaging ? onPageChange : undefined;

  return useMemo(
    () => ({
      isLoadingRuns: isLoadingPage,
      isLoadingRunsPolled: isPollTaskActive && !isPageMismatched,
      loadMoreRuns,
      onPageChange: onOffsetPageChange,
      runs: pageRuns,
      pinnedRuns,
      totalRuns,
      runFilterErrorMessage,
    }),
    [
      isLoadingPage,
      isPageMismatched,
      isPollTaskActive,
      loadMoreRuns,
      onOffsetPageChange,
      pageRuns,
      pinnedRuns,
      totalRuns,
      runFilterErrorMessage,
    ]
  );
};
